/**
 * StepStart — step 1 of the agent-creation flow: name, language, hosting.
 *
 * "Create agent" calls POST /onboarding/create_bot, which returns a COMPLETED
 * agent instantly (prompt / widget / default skills finish in the background).
 * We show a short build sequence — real signal: we wait until the widget
 * config exists (the background finisher's last write) or 12s, whichever comes
 * first — then continue to the next step for that agent.
 *
 * Webflow Marketplace compliance: an account that is Webflow-connected never
 * sees the hosting question (it is Webflow-hosted by definition); answering
 * "Webflow" stores bots.webflow_hosted so the Knowledge step hides every
 * website-training surface for this agent.
 */
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField, MenuItem } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe, faCheck } from '@fortawesome/free-solid-svg-icons';
import { authFetch } from '../../../utils/api';
import { ASK_HOSTING_QUESTION, URL_GATE_DISABLED } from '../../../utils/webflowGate';
import { API, LANGUAGES, FIELD_SX } from '../steps.js';
import StepFooter from './StepFooter.js';
import './StepStart.css';

const BUILD_MIN_MS = 2400;   // the build sequence is shown at least this long
const BUILD_MAX_MS = 12000;  // …and we stop waiting for the widget config after this

const UI_TO_AGENT_LANGUAGE = { en: 'English', de: 'German', fr: 'French', es: 'Spanish', it: 'Italian', pt: 'Portuguese', nl: 'Dutch', pl: 'Polish' };

/* Radio indicator: an SVG ring on the left of a choice row (empty = pick one;
   selected = purple ring with a centred dot). Geometry lives in the SVG so the
   dot is exactly centred regardless of font/zoom. */
function Radio({ checked }) {
  return (
    <svg className={`ac-radio ${checked ? 'is-checked' : ''}`} viewBox="0 0 22 22" width="22" height="22" aria-hidden="true" focusable="false">
      <circle cx="11" cy="11" r="9.5" className="ac-radio-ring" fill="none" strokeWidth="1.6" />
      <circle cx="11" cy="11" r="5" className="ac-radio-dot" />
    </svg>
  );
}

export default function StepStart({ goTo }) {
  const { t, i18n } = useTranslation();

  const isWebflowUser = localStorage.getItem('webflow_connected') === '1'
    || localStorage.getItem('webflow_history') === '1';
  const [name, setName] = useState('');
  const [language, setLanguage] = useState(() => UI_TO_AGENT_LANGUAGE[(i18n.language || 'en').split('-')[0]] || 'English');
  const [webflowHosted, setWebflowHosted] = useState(isWebflowUser ? true : null);
  const [attempted, setAttempted] = useState(false); // Create clicked with something missing
  const [err, setErr] = useState('');
  const [building, setBuilding] = useState(false);
  const [buildStage, setBuildStage] = useState(0); // 0..2 running, 3 = all done
  const nameRef = useRef(null);
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);

  const missingName = name.trim().length === 0;
  // The hosting question is not asked while the gate is disabled or the
  // question is switched off (utils/webflowGate.js), so it can never block
  // the create button then.
  const askHosting = !URL_GATE_DISABLED && ASK_HOSTING_QUESTION && !isWebflowUser;
  const missingHosting = askHosting && webflowHosted === null;
  const canCreate = !missingName && !missingHosting;
  // Only after a click: which fields are marked red + the note on the button row.
  const nameError = attempted && missingName;
  const hostingError = attempted && missingHosting;
  const missingMessage = attempted && !canCreate
    ? (missingName && missingHosting ? t('ac_start_missing_both') : missingName ? t('ac_start_missing_name') : t('ac_start_missing_hosting'))
    : null;

  async function waitForWidget(botId) {
    const started = Date.now();
    // Readiness poll: a 404 here just means "not written yet". Use the raw fetch
    // (with our own Bearer) so the global interceptor doesn't pop its error
    // modal on the expected 404s.
    const raw = window._originalFetch || fetch;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      try {
        const r = await raw(`${API}widget_configuration/${encodeURIComponent(botId)}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (r.ok) return true;
      } catch { /* retry */ }
      if (Date.now() - started > BUILD_MAX_MS) return false;
      await new Promise((res) => setTimeout(res, 800));
    }
  }

  async function createAgent() {
    if (!canCreate) {
      setAttempted(true);
      if (missingName) nameRef.current?.focus();
      return;
    }
    setErr('');
    setBuilding(true);
    setBuildStage(0);
    const stageTimer = setInterval(() => setBuildStage((s) => Math.min(2, s + 1)), 900);
    const t0 = Date.now();
    try {
      const res = await authFetch(`${API}onboarding/create_bot`, {
        method: 'POST',
        body: JSON.stringify({ bot_name: name.trim(), language, webflow_hosted: !!webflowHosted }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.bot_id) throw new Error(data.error || 'create failed');
      if (window.dataLayer) window.dataLayer.push({ event: 'bot_creation_started' });
      await waitForWidget(data.bot_id);
      const wait = Math.max(0, BUILD_MIN_MS - (Date.now() - t0));
      if (wait) await new Promise((r) => setTimeout(r, wait));
      clearInterval(stageTimer);
      if (!aliveRef.current) return;
      setBuildStage(3);
      setTimeout(() => { if (aliveRef.current) goTo('knowledge', { botId: data.bot_id, replace: true }); }, 450);
    } catch (e) {
      clearInterval(stageTimer);
      console.error('create agent failed', e);
      if (!aliveRef.current) return;
      setBuilding(false);
      setErr(t('ac_start_create_failed'));
    }
  }

  if (building) {
    const rows = [t('ac_build_1'), t('ac_build_2'), t('ac_build_3')];
    return (
      <div className="ac-building">
        <span className="ac-spinner ac-spinner-lg" />
        <h2 className="ac-title">{t('ac_build_title', { name: name.trim() })}</h2>
        <p className="ac-sub">{t('ac_build_sub')}</p>
        <ul className="ac-build-list">
          {rows.map((label, i) => {
            const cls = buildStage > i ? 'ok' : buildStage === i ? 'on' : '';
            return (
              <li key={label} className={cls}>
                <span className="ac-build-dot">{cls === 'ok' ? <FontAwesomeIcon icon={faCheck} /> : null}</span>
                <span>{label}</span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  return (
    <>
      <header className="ac-head">
        <h2 className="ac-title">{t('ac_start_title')}</h2>
        <p className="ac-sub">{t('ac_start_sub')}</p>
      </header>

      <div className="ac-body">
      <div className="ac-start-form">
        <TextField
          inputRef={nameRef}
          autoFocus
          label={t('ac_start_name_label')}
          placeholder={t('ac_start_name_ph')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') createAgent(); }}
          error={nameError}
          helperText={nameError ? t('ac_start_name_required') : ' '}
          fullWidth
          variant="outlined"
          inputProps={{ maxLength: 60 }}
          sx={FIELD_SX}
        />
        <TextField
          select
          label={t('ac_start_language_label')}
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          helperText=" "
          variant="outlined"
          sx={FIELD_SX}
        >
          {LANGUAGES.map((l) => <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>)}
        </TextField>
      </div>

      {/* Hosting question / Webflow note: skipped entirely while the gate is
          disabled (utils/webflowGate.js) — webflow_hosted still persists via
          the state's initial value for Webflow-connected accounts. */}
      {URL_GATE_DISABLED || !(isWebflowUser || askHosting) ? null : isWebflowUser ? (
        <div className="ac-note">
          <img src="/webflow-logo.png" alt="Webflow" />
          <span>{t('addbot_hosting_webflow_note')}</span>
        </div>
      ) : (
        <section className="ac-start-hosting">
          <h3 className={`ac-label ${hostingError ? 'is-error' : ''}`}>{t('ac_start_hosting_q')}</h3>
          <div className={`ac-choice-list ${hostingError ? 'is-error' : ''}`} role="radiogroup" aria-label={t('ac_start_hosting_q')} aria-invalid={hostingError || undefined}>
            <button type="button" role="radio" aria-checked={webflowHosted === false}
              className={`ac-choice ${webflowHosted === false ? 'is-selected' : ''}`}
              onClick={() => setWebflowHosted((v) => (v === false ? null : false))}>
              <Radio checked={webflowHosted === false} />
              <span className="ac-choice-icon"><FontAwesomeIcon icon={faGlobe} /></span>
              <span className="ac-choice-text">
                <span className="ac-choice-title">{t('ac_start_hosting_other')}</span>
                <span className="ac-choice-desc">{t('ac_start_hosting_other_desc')}</span>
              </span>
            </button>
            <button type="button" role="radio" aria-checked={webflowHosted === true}
              className={`ac-choice ${webflowHosted === true ? 'is-selected' : ''}`}
              onClick={() => setWebflowHosted((v) => (v === true ? null : true))}>
              <Radio checked={webflowHosted === true} />
              <span className="ac-choice-icon"><img src="/webflow-logo.png" alt="" /></span>
              <span className="ac-choice-text">
                <span className="ac-choice-title">{t('ac_start_hosting_webflow')}</span>
                <span className="ac-choice-desc">{t('ac_start_hosting_webflow_desc')}</span>
              </span>
            </button>
          </div>
        </section>
      )}

      {err && <div className="ac-error">{err}</div>}
      </div>

      <StepFooter primaryLabel={t('ac_start_cta')} onPrimary={createAgent} message={missingMessage} />
    </>
  );
}
